import React from 'react';
import { PREMIUM_DECOR, PREMIUM_DECOR_ORDER, type PremiumDecorId } from './premiumDecorData';
import { pvDuration, pvDelay, type PvStyle } from '../motionStyles';

interface PremiumDecorProps {
  variant: PremiumDecorId;
  reducedMotion: boolean;
  quality: 'low' | 'balanced' | 'high';
  speed: number;
  active?: boolean;
  dimmed?: boolean;
}

function anim(duration: number, delay: number, speed: number): PvStyle {
  return { '--pv-duration': pvDuration(duration, speed), '--pv-delay': pvDelay(delay) };
}

/**
 * One premium decoration. Ambient motion only: a glow pulse on the lantern, a
 * whisper of drift on the grotto and a sparkle on the treasure chest.
 */
export function PremiumDecor({ variant, reducedMotion, quality, speed, active = false, dimmed = false }: PremiumDecorProps) {
  const def = PREMIUM_DECOR[variant];
  const p = def.palette;
  const m = def.motion;
  const reduced = reducedMotion;
  const quiet = quality === 'low';

  const rootClass = [
    'pv-asset',
    active ? 'is-active' : '',
    dimmed ? 'is-dim' : '',
    reduced ? 'pv-reduced' : '',
    quiet ? 'pv-quiet' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const uid = `pvdecor-${variant}`;

  if (variant === 'lantern') {
    return (
      <div className={rootClass} style={{ width: '100%' }}>
        <svg viewBox="0 0 120 180" style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="ThriveCove premium glow lantern">
          <defs>
            <radialGradient id={`${uid}-glow`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={p.glow} stopOpacity="0.95" />
              <stop offset="45%" stopColor={p.accent} stopOpacity="0.45" />
              <stop offset="100%" stopColor={p.accent} stopOpacity="0" />
            </radialGradient>
            <linearGradient id={`${uid}-metal`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={p.baseLight} />
              <stop offset="55%" stopColor={p.base} />
              <stop offset="100%" stopColor={p.baseDark} />
            </linearGradient>
          </defs>

          <ellipse className="pv-anim pv-glow" cx={60} cy={96} rx={32} ry={36} fill={`url(#${uid}-glow)`} style={{ transformBox: 'fill-box', transformOrigin: 'center', ...anim(m.glow, m.delay, speed) } as PvStyle} />

          <g className="pv-anim pv-float-soft" style={{ transformBox: 'fill-box', transformOrigin: 'center', ...anim(m.float, m.delay, speed) } as PvStyle}>
            <path d="M 56 50 Q 60 44 64 50" fill="none" stroke={p.baseLight} strokeWidth="2.4" strokeLinecap="round" />
            <ellipse cx={60} cy={58} rx={5} ry={3} fill={`url(#${uid}-metal)`} />
            <path d="M 42 92 Q 42 66 60 52 Q 78 66 78 92 Q 78 116 60 128 Q 42 116 42 92 Z" fill={`url(#${uid}-metal)`} />
            <path d="M 46 52 Q 60 42 74 52 Q 66 48 60 46 Q 54 48 46 52 Z" fill={p.baseLight} />
            <path d="M 44 112 Q 60 122 76 112" fill="none" stroke={p.base} strokeWidth="3" strokeLinecap="round" />
            <path d="M 48 70 Q 50 74 54 98 Q 60 100 66 98 Q 70 74 72 70" fill={p.glow} opacity="0.35" />
            <ellipse cx={60} cy={88} rx={7} ry={10} fill={p.glow} opacity="0.9" />
            <ellipse cx={60} cy={84} rx={3.4} ry={6} fill="#ffffff" opacity="0.9" />
          </g>

          {!quiet && (
            <g>
              <circle className="pv-anim pv-shimmer pv-secondary" cx={44} cy={78} r={2.6} fill={p.shimmer} style={anim(m.shimmer, m.delay, speed)} />
              <circle className="pv-anim pv-shimmer pv-secondary" cx={76} cy={64} r={2} fill={p.shimmer} style={anim(m.shimmer + 0.8, m.delay + 0.6, speed)} />
              <circle className="pv-anim pv-shimmer pv-secondary" cx={68} cy={104} r={2.2} fill={p.shimmer} style={anim(m.shimmer + 1.4, m.delay + 1.1, speed)} />
            </g>
          )}
        </svg>
      </div>
    );
  }

  if (variant === 'grotto') {
    return (
      <div className={rootClass} style={{ width: '100%' }}>
        <svg viewBox="0 0 190 140" style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="ThriveCove premium rock grotto">
          <defs>
            <linearGradient id={`${uid}-rock`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={p.baseLight} />
              <stop offset="55%" stopColor={p.base} />
              <stop offset="100%" stopColor={p.baseDark} />
            </linearGradient>
            <radialGradient id={`${uid}-cave`} cx="50%" cy="45%" r="60%">
              <stop offset="0%" stopColor="#0b1f28" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#0b1f28" stopOpacity="0.45" />
            </radialGradient>
          </defs>

          <g className="pv-anim pv-float-soft" style={{ transformBox: 'fill-box', transformOrigin: 'bottom center', ...anim(m.float, m.delay, speed) } as PvStyle}>
            <path d="M 18 128 Q 10 100 26 72 Q 44 44 80 46 Q 118 48 140 74 Q 158 98 150 128 Q 120 138 80 138 Q 40 138 18 128 Z" fill={`url(#${uid}-rock)`} />
            <path d="M 58 128 Q 56 96 78 84 Q 100 96 98 128 Z" fill={`url(#${uid}-cave)`} />
            <path d="M 26 74 Q 44 50 78 50" fill="none" stroke={p.baseLight} strokeWidth="2" opacity="0.4" strokeLinecap="round" />
            <path d="M 32 118 Q 28 104 34 92" fill="none" stroke={p.detail} strokeWidth="4" opacity="0.55" strokeLinecap="round" className="pv-secondary" />
            <path d="M 138 118 Q 144 104 138 92" fill="none" stroke={p.detail} strokeWidth="4" opacity="0.5" strokeLinecap="round" className="pv-secondary" />
            <ellipse cx={44} cy={130} rx={10} ry={5} fill={p.baseDark} opacity="0.6" />
            <ellipse cx={126} cy={131} rx={8} ry={4} fill={p.baseDark} opacity="0.6" />
          </g>

          {!quiet && (
            <g>
              <circle className="pv-anim pv-shimmer pv-secondary" cx={34} cy={88} r={2.2} fill={p.shimmer} style={anim(m.shimmer, m.delay + 0.4, speed)} />
              <circle className="pv-anim pv-shimmer pv-secondary" cx={148} cy={96} r={1.8} fill={p.shimmer} style={anim(m.shimmer + 1, m.delay + 1, speed)} />
            </g>
          )}
        </svg>
      </div>
    );
  }

  // treasure
  return (
    <div className={rootClass} style={{ width: '100%' }}>
      <svg viewBox="0 0 150 130" style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="ThriveCove premium treasure chest">
        <defs>
          <linearGradient id={`${uid}-wood`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={p.baseLight} />
            <stop offset="60%" stopColor={p.base} />
            <stop offset="100%" stopColor={p.baseDark} />
          </linearGradient>
          <linearGradient id={`${uid}-gold`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={p.glow} />
            <stop offset="100%" stopColor={p.detail} />
          </linearGradient>
        </defs>

        <ellipse cx={75} cy={122} rx={52} ry={8} fill="#153f42" opacity="0.16" />

        <g className="pv-anim pv-float-soft" style={{ transformBox: 'fill-box', transformOrigin: 'bottom center', ...anim(m.float, m.delay, speed) } as PvStyle}>
          <path d="M 28 70 L 122 70 L 122 112 Q 122 120 112 120 L 38 120 Q 28 120 28 112 Z" fill={`url(#${uid}-wood)`} />
          <path d="M 34 70 L 34 120 M 75 70 L 75 120 M 116 70 L 116 120" stroke={`url(#${uid}-gold)`} strokeWidth="5" opacity="0.85" />

          <g className="pv-anim pv-lid pv-secondary" style={{ transformBox: 'fill-box', transformOrigin: 'bottom center', ...anim(m.shimmer + 1.6, m.delay, speed) } as PvStyle}>
            <path d="M 24 70 Q 24 42 75 40 Q 126 42 126 70 Z" fill={`url(#${uid}-wood)`} />
            <path d="M 24 70 Q 24 42 75 40 Q 126 42 126 70" fill="none" stroke={p.detail} strokeWidth="3" opacity="0.7" />
          </g>

          <rect x={67} y={76} width={16} height={20} rx={4} fill={`url(#${uid}-gold)`} />
          <circle cx={75} cy={82} r={2.4} fill={p.baseDark} />
          <circle cx={55} cy={63} r={3} fill={p.accent} opacity="0.9" />
          <circle cx={95} cy={63} r={3} fill={p.accent} opacity="0.9" />
        </g>

        {!quiet && (
          <g>
            <path className="pv-anim pv-shimmer pv-secondary" d="M 46 46 l 2 5 5 2 -5 2 -2 5 -2 -5 -5 -2 5 -2 z" fill={p.shimmer} style={anim(m.shimmer, m.delay, speed)} />
            <path className="pv-anim pv-shimmer pv-secondary" d="M 104 40 l 1.6 4 4 1.6 -4 1.6 -1.6 4 -1.6 -4 -4 -1.6 4 -1.6 z" fill={p.shimmer} style={anim(m.shimmer + 1.2, m.delay + 0.8, speed)} />
          </g>
        )}
      </svg>
    </div>
  );
}

export { PREMIUM_DECOR, PREMIUM_DECOR_ORDER };
